// Sizing and shape for media objects — TVs and projector setups.
//
// Split from editorState.ts the same way windowGeom.ts is: the pure geometry
// and catalog constants live here, the document entry (`MediaEntry`, with its
// id and world transform) lives in editorState.ts and imports from this file.
//
// The one rule that shapes everything below: a media object is sized by its
// DIAGONAL, because that is the only number this category is ever sold or
// discussed in. Width and height are always derived and never stored, so they
// cannot drift from the diagonal the user actually chose.

import type { PanelSize } from './adminTypes';

export type MediaKind = 'tv' | 'projector';

/**
 * Picture shape.
 *
 * Three, not four: `21:9` is deliberately absent because 21/9 = 2.333 sits
 * within 0.7% of `2.35:1`, which is a real and differently-named thing
 * (cinemascope). Offering both would be two controls for one shape.
 */
export type MediaAspect = '16:9' | '2.35:1' | '4:3';

/** width ÷ height for each aspect. */
const ASPECT_RATIO: Record<MediaAspect, number> = {
  '16:9': 16 / 9,
  '2.35:1': 2.35,
  '4:3': 4 / 3,
};

export const MEDIA_ASPECTS: readonly MediaAspect[] = ['16:9', '2.35:1', '4:3'];

export function aspectRatio(aspect: MediaAspect): number {
  return ASPECT_RATIO[aspect] ?? ASPECT_RATIO['16:9'];
}

export const INCH_M = 0.0254;

export function inchesToM(inches: number): number {
  return inches * INCH_M;
}

export function mToInches(metres: number): number {
  return metres / INCH_M;
}

/** The sizing half of a media object — everything `mediaSize` needs. */
export interface MediaGeom {
  diagonalM: number;
  aspect: MediaAspect;
}

/** Acoustic OBB depth of a TV panel, metres — a flat panel plus the
 *  electronics bulge behind it. One value for every size; the difference
 *  between a slim OLED and a thick LED is far below the solver's voxel pitch. */
export const TV_DEPTH_M = 0.06;

/** Acoustic OBB depth of a fixed-frame projection screen, metres — the frame,
 *  not the fabric. */
export const SCREEN_DEPTH_M = 0.05;

/** A typical home projector body. Fixed: nobody sizes a projector, and its
 *  dimensions barely vary across the category. */
export const PROJECTOR_BODY: PanelSize = { w: 0.33, h: 0.13, d: 0.28 };

/** Diagonals the market actually sells, inches. Chips, not a slider — a
 *  control that can land on 63.5" is offering precision that doesn't exist. */
export const TV_DIAGONAL_LADDER_IN: readonly number[] = [43, 50, 55, 65, 75, 85, 98];
export const SCREEN_DIAGONAL_LADDER_IN: readonly number[] = [92, 100, 110, 120, 135, 150];

/** Where a new one starts, inches, when nothing says otherwise: 65" is the
 *  median TV sold; 120" the median fixed-frame screen. A projector model
 *  publishes no picture size (its picture is its screen's), so a projector
 *  added from the catalogue starts here too. */
export const DEFAULT_DIAGONAL_IN: Record<MediaKind, number> = { tv: 65, projector: 120 };

/** Mount standoff presets, metres — flush, slim, tilting, articulating. */
export const MEDIA_STANDOFF_PRESETS_M: readonly number[] = [0.025, 0.04, 0.06, 0.10];

/** Ceiling drop-rod presets for a projector, metres. */
export const PROJECTOR_DROP_PRESETS_M: readonly number[] = [0.10, 0.20, 0.35, 0.60];

/** 24" — smaller than any TV worth modelling acoustically. */
export const MIN_DIAGONAL_M = inchesToM(24);
/** 200" — past the largest commercially available projection screen. */
export const MAX_DIAGONAL_M = inchesToM(200);

/** Keep a diagonal inside the range real hardware occupies, and never let a
 *  non-finite value through — a NaN here becomes NaN geometry, which renders
 *  as nothing at all and is very hard to trace back. */
export function clampDiagonalM(m: number): number {
  if (!Number.isFinite(m)) return inchesToM(65);
  return Math.min(MAX_DIAGONAL_M, Math.max(MIN_DIAGONAL_M, m));
}

/**
 * Diagonal + aspect → width and height, metres.
 *
 * From w² + h² = D² with w = r·h: h = D / √(r² + 1), w = r·h. For 16:9 that
 * is w ≈ 0.8716·D and h ≈ 0.4903·D, so a 65" TV is 1.439 × 0.809 m.
 */
export function diagonalToSize(
  diagonalM: number, aspect: MediaAspect,
): { w: number; h: number } {
  const d = clampDiagonalM(diagonalM);
  const r = aspectRatio(aspect);
  const h = d / Math.sqrt(r * r + 1);
  return { w: r * h, h };
}

/** Inverse of `diagonalToSize` — the corner-to-corner measure of a w × h rect. */
export function sizeToDiagonal(w: number, h: number): number {
  return Math.hypot(w, h);
}

/** A TV panel's acoustic OBB. */
export function tvSize(g: MediaGeom): PanelSize {
  return { ...diagonalToSize(g.diagonalM, g.aspect), d: TV_DEPTH_M };
}

/** A projection screen's acoustic OBB. */
export function screenSize(g: MediaGeom): PanelSize {
  return { ...diagonalToSize(g.diagonalM, g.aspect), d: SCREEN_DEPTH_M };
}

/**
 * The OBB of the DEVICE at `entry.position` — the panel for a TV, the body for
 * a projector. A projector's picture is not where the projector is, which is
 * exactly why this and `mediaPictureSize` are two functions.
 */
export function mediaDeviceSize(kind: MediaKind, g: MediaGeom): PanelSize {
  return kind === 'projector' ? { ...PROJECTOR_BODY } : tvSize(g);
}

/**
 * The OBB of the PICTURE — the panel for a TV, the screen for a projector.
 * For a projector this sits on the target wall, not at `entry.position`.
 */
export function mediaPictureSize(kind: MediaKind, g: MediaGeom): PanelSize {
  return kind === 'projector' ? screenSize(g) : tvSize(g);
}
